/*
 * <heat-rail>: macOS-dock magnification for the PepperZ heat rail plus a cursor
 * tracker that rides the heat scale.
 *
 * Fine pointer (mouse): items scale by Gaussian proximity to the cursor X so
 * neighbours grow proportionally, and the tracker follows the cursor.
 * Coarse pointer (touch): the rail scrolls horizontally — items scale by
 * proximity to the visible centre and the tracker maps to scroll position.
 * prefers-reduced-motion: no scaling; tracker is scroll-linked only.
 * The JS is the single source of truth for the transform; CSS only eases it.
 */
(function () {
  'use strict';

  var POINTER_AMP = 0.13; // extra scale directly under the cursor (very subtle)
  var TOUCH_AMP = 0.09;
  var SIGMA = 115; // px falloff radius (wider = gentler spread)

  function clamp(v, a, b) {
    return Math.max(a, Math.min(b, v));
  }

  class HeatRail extends HTMLElement {
    connectedCallback() {
      this.items = Array.from(this.querySelectorAll('.home-heat__item'));
      this.row = this.querySelector('[data-heat-items]');
      this.cursor = this.querySelector('[data-heat-cursor]');
      this.meter = this.cursor ? this.cursor.parentElement : null;
      if (!this.items.length) return;

      this.reduceMQ = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.fineMQ = window.matchMedia('(hover: hover) and (pointer: fine)');
      this.raf = null;

      this.bindPointer = this.bindPointer.bind(this);
      this.bindLeave = this.bindLeave.bind(this);
      this.bindScroll = this.bindScroll.bind(this);
      this.setup = this.setup.bind(this);

      this.setup();
      this.reduceMQ.addEventListener('change', this.setup);
      this.fineMQ.addEventListener('change', this.setup);
      window.addEventListener('resize', this.setup, { passive: true });
    }

    disconnectedCallback() {
      this.reduceMQ.removeEventListener('change', this.setup);
      this.fineMQ.removeEventListener('change', this.setup);
      window.removeEventListener('resize', this.setup);
      this.unbind();
    }

    unbind() {
      this.removeEventListener('pointermove', this.bindPointer);
      this.removeEventListener('pointerleave', this.bindLeave);
      if (this.row) this.row.removeEventListener('scroll', this.bindScroll);
      if (this.raf) cancelAnimationFrame(this.raf);
      this.reset();
    }

    setup() {
      this.unbind();
      if (this.reduceMQ.matches) {
        // No scaling; tracker follows scroll position only.
        if (this.row) {
          this.row.addEventListener('scroll', this.bindScroll, { passive: true });
          this.updateTrackerFromScroll();
        }
        return;
      }
      if (this.fineMQ.matches) {
        this.addEventListener('pointermove', this.bindPointer, { passive: true });
        this.addEventListener('pointerleave', this.bindLeave, { passive: true });
      } else if (this.row) {
        this.row.addEventListener('scroll', this.bindScroll, { passive: true });
        this.bindScroll();
      }
    }

    reset() {
      this.items.forEach(function (it) {
        it.style.transform = '';
      });
      if (this.cursor) this.cursor.classList.remove('is-active');
    }

    // Scale every item by its distance from focusX (both relative to this element).
    applyScales(focusX, amp) {
      var host = this.getBoundingClientRect();
      this.items.forEach(function (it) {
        var r = it.getBoundingClientRect();
        var cx = r.left + r.width / 2 - host.left;
        var d = cx - focusX;
        var s = 1 + amp * Math.exp(-(d * d) / (2 * SIGMA * SIGMA));
        it.style.transform = 'scale(' + s.toFixed(3) + ')';
      });
    }

    moveTracker(x) {
      if (!this.cursor || !this.meter) return;
      var host = this.getBoundingClientRect();
      var m = this.meter.getBoundingClientRect();
      var left = clamp(x - (m.left - host.left), 0, m.width);
      this.cursor.style.left = left + 'px';
      this.cursor.classList.add('is-active');
    }

    bindPointer(event) {
      var self = this;
      var host = this.getBoundingClientRect();
      var x = event.clientX - host.left;
      if (this.raf) cancelAnimationFrame(this.raf);
      this.raf = requestAnimationFrame(function () {
        self.applyScales(x, POINTER_AMP);
        self.moveTracker(x);
      });
    }

    bindLeave() {
      if (this.raf) cancelAnimationFrame(this.raf);
      this.reset();
    }

    bindScroll() {
      var self = this;
      if (this.raf) cancelAnimationFrame(this.raf);
      this.raf = requestAnimationFrame(function () {
        if (!self.reduceMQ.matches) {
          // Item nearest the visible centre grows.
          var host = self.getBoundingClientRect();
          self.applyScales(host.width / 2, TOUCH_AMP);
        }
        self.updateTrackerFromScroll();
      });
    }

    updateTrackerFromScroll() {
      if (!this.cursor || !this.meter || !this.row) return;
      var max = this.row.scrollWidth - this.row.clientWidth;
      var frac = max > 0 ? this.row.scrollLeft / max : 0;
      var m = this.meter.getBoundingClientRect();
      this.cursor.style.left = frac * m.width + 'px';
      this.cursor.classList.toggle('is-active', max > 0);
    }
  }

  if (!window.customElements.get('heat-rail')) {
    window.customElements.define('heat-rail', HeatRail);
  }
})();
